import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, User, Phone, Store, Bike, CalendarDays, Minus, Plus, ShoppingCart, MessageCircle, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, rm, errMsg, timeSlots } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ClosedNotice } from "@/components/customer/ClosedNotice";
import { PaymentPicker } from "@/components/customer/PaymentPicker";

const Field = ({ icon: Icon, label, children }) => (
  <label className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2 focus-within:ring-2 focus-within:ring-[#B91C1C]">
    <Icon className="w-5 h-5 text-slate-600" />
    <span className="flex-1">
      <span className="block text-[11px] text-slate-500">{label}</span>
      {children}
    </span>
  </label>
);

function CartLine({ it, setQty }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100" data-testid={`cart-line-${it.menu_id}`}>
      <img src={it.image || "/img/logo.png"} alt="" className={`w-14 h-14 rounded-lg ${it.image ? "object-cover" : "object-contain p-1.5 bg-amber-50"}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">{it.name}</p>
        {it.option && <p className="text-xs text-slate-500">{it.option}</p>}
        {it.note && <p className="text-xs text-slate-500 italic truncate">"{it.note}"</p>}
        <div className="flex items-center gap-3 mt-1">
          <button onClick={() => setQty(it.key, it.qty - 1)} data-testid={`cart-minus-${it.menu_id}`} className="w-6 h-6 rounded-full border flex items-center justify-center"><Minus className="w-3 h-3" /></button>
          <span className="text-sm font-semibold">×{it.qty}</span>
          <button onClick={() => setQty(it.key, it.qty + 1)} data-testid={`cart-plus-${it.menu_id}`} className="w-6 h-6 rounded-full border flex items-center justify-center"><Plus className="w-3 h-3" /></button>
        </div>
      </div>
      <span className="text-sm font-bold">{rm(it.unit_price * it.qty)}</span>
    </div>
  );
}

const ModeBtn = ({ active, onClick, icon: Icon, label, id, disabled, sub }) => (
  <button onClick={onClick} data-testid={id} disabled={disabled}
    className={`h-16 rounded-xl flex flex-col items-center justify-center gap-1 text-sm font-medium border transition-colors disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed ${active ? "bg-[#B91C1C] text-white border-[#B91C1C] shadow-md" : "bg-white border-slate-200"}`}>
    <Icon className="w-5 h-5" />{label}
    {sub && <span className="text-[10px] leading-none">{sub}</span>}
  </button>
);

export default function Cart() {
  const nav = useNavigate();
  const { items, setQty, count, total, clear, profile, setProfile, addHistory } = useCart();
  const [shop, setShop] = useState(null);
  const slots = useMemo(() => timeSlots(shop), [shop]);
  const [mode, setMode] = useState("pickup");
  const [address, setAddress] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState("tunai");
  const [email, setEmail] = useState(() => localStorage.getItem("alfaz_email") || "");
  const [directPick, setDirect] = useState("qr");
  const [proof, setProof] = useState("");
  const direct = shop?.qr_payment && shop?.online_payment ? directPick : shop?.qr_payment ? "qr" : "online";
  const online = pay === "terus" && direct === "online" && shop?.online_payment;
  const qr = pay === "terus" && direct === "qr" && shop?.qr_payment;
  const deliveryOn = shop?.delivery_enabled ?? true;
  const closed = shop ? !shop.is_open : false;

  useEffect(() => {
    api.get("/public/settings").then((r) => {
      setShop(r.data);
      if (!r.data.delivery_enabled) setMode("pickup");
    }).catch(() => {});
  }, []);
  useEffect(() => { setTime((t) => (slots.includes(t) ? t : slots[0] || "")); }, [slots]);

  if (items.length === 0)
    return (
      <div className="flex flex-col items-center justify-center pt-32 px-8 text-center" data-testid="cart-empty">
        <ShoppingCart className="w-14 h-14 text-slate-300" />
        <p className="font-display font-bold text-lg mt-4">Troli anda kosong</p>
        <p className="text-sm text-slate-500 mt-1">Pilih menu kegemaran anda dahulu.</p>
        <button onClick={() => nav("/")} data-testid="cart-browse-button" className="mt-6 px-6 h-11 rounded-full bg-[#B91C1C] text-white font-semibold">Lihat Menu</button>
      </div>
    );

  const submit = async () => {
    if (!profile.name.trim() || profile.phone.trim().length < 9) return toast.error("Sila isi nama dan no. telefon yang sah");
    if (mode === "delivery" && !address.trim()) return toast.error("Sila isi alamat penghantaran");
    if (closed) return toast.error("Kedai Tutup");
    if (!time) return toast.error("Sila pilih masa");
    if (online && !/^\S+@\S+\.\S+$/.test(email.trim())) return toast.error("Sila isi emel yang sah untuk bayaran online");
    if (qr && !proof) return toast.error("Sila muat naik bukti bayaran QR");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: profile.name, phone: profile.phone, mode, address, pickup_time: time,
        items: items.map((i) => ({ menu_id: i.menu_id, option: i.option, qty: i.qty, note: i.note })),
        payment_method: online ? "online" : qr ? "qr" : "tunai", email: online ? email.trim() : "", payment_proof: qr ? proof : "",
      });
      addHistory(data.id);
      clear();
      if (data.payment_url) {
        localStorage.setItem("alfaz_email", email.trim());
        window.location.href = data.payment_url;
        return;
      }
      nav(`/pesanan/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 px-4 h-14 bg-white border-b">
        <button onClick={() => nav(-1)} data-testid="cart-back-button"><ChevronLeft className="w-6 h-6" /></button>
        <h1 className="flex-1 text-center font-display font-bold text-lg pr-6">Ringkasan Pesanan</h1>
      </div>
      <div className="px-5 pb-8">
        {items.map((it) => <CartLine key={it.key} it={it} setQty={setQty} />)}
        <div className="flex justify-between items-center py-4 border-b">
          <span className="font-semibold">Jumlah <span className="font-normal text-slate-500 text-sm">({count} item)</span></span>
          <span className="font-display font-extrabold text-lg" data-testid="cart-total">{rm(total)}</span>
        </div>
        <h2 className="font-display font-bold text-base mt-5 mb-3">Maklumat Pelanggan</h2>
        <div className="space-y-3">
          <Field icon={User} label="Nama *">
            <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="customer-name-input" placeholder="Nama anda" className="w-full outline-none text-sm font-medium bg-transparent" />
          </Field>
          <Field icon={Phone} label="No. Telefon (WhatsApp) *">
            <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="customer-phone-input" inputMode="tel" placeholder="012-345 6789" className="w-full outline-none text-sm font-medium bg-transparent" />
          </Field>
        </div>
        <h2 className="font-display font-bold text-base mt-5 mb-3">Ambil / Penghantaran</h2>
        <div className="grid grid-cols-2 gap-3">
          <ModeBtn id="mode-pickup" active={mode === "pickup"} onClick={() => setMode("pickup")} icon={Store} label="Ambil di Restoran" />
          <ModeBtn id="mode-delivery" active={mode === "delivery"} disabled={!deliveryOn} onClick={() => setMode("delivery")} icon={Bike} label="Penghantaran" sub={!deliveryOn ? "Tidak tersedia" : null} />
        </div>
        {!deliveryOn && <p className="mt-2 text-xs text-slate-500" data-testid="delivery-unavailable-msg">Penghantaran tidak tersedia buat masa ini.</p>}
        {mode === "delivery" && (
          <textarea value={address} onChange={(e) => setAddress(e.target.value)} data-testid="delivery-address-input" rows={2} placeholder="Alamat penghantaran"
            className="mt-3 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:ring-2 focus:ring-[#B91C1C]" />
        )}
        <h2 className="font-display font-bold text-base mt-5 mb-3">Tarikh &amp; Masa {mode === "pickup" ? "Ambil" : "Hantar"}</h2>
        <Select value={closed ? "" : time} onValueChange={setTime} disabled={closed}>
          <SelectTrigger data-testid="time-select" className="h-12 rounded-xl bg-white justify-start gap-3 [&>span]:flex-1 [&>span]:text-left">
            <CalendarDays className="w-5 h-5 text-slate-600 shrink-0" />
            <SelectValue placeholder="Pilih masa" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {slots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {closed && <ClosedNotice shop={shop} />}
        {(shop?.online_payment || shop?.qr_payment) && (
          <PaymentPicker shop={shop} pay={pay} setPay={setPay} direct={direct} setDirect={setDirect}
            email={email} setEmail={setEmail} proof={proof} setProof={setProof} Field={Field} />
        )}
        <button onClick={submit} disabled={busy || closed} data-testid="submit-order-button"
          className="mt-6 w-full h-16 rounded-2xl bg-[#B91C1C] text-white flex items-center px-5 gap-4 shadow-lg active:scale-[0.98] transition-transform disabled:opacity-70 disabled:bg-slate-400">
          {busy ? <Loader2 className="w-8 h-8 animate-spin" /> : online ? <CreditCard className="w-8 h-8" /> : <MessageCircle className="w-8 h-8" />}
          <span className="flex-1 text-left leading-tight">
            <span className="block font-display font-bold text-lg">{closed ? "Kedai Tutup" : online ? "Bayar & Hantar Pesanan" : "Hantar Pesanan"}</span>
            <span className="block text-xs text-white/80">{online ? `${rm(total)} · ke halaman bayaran` : "(ke WhatsApp)"}</span>
          </span>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}
